/** @module store/measurements */

import * as actions from '@/store/modules/measurements/actions'
import * as getters from '@/store/modules/measurements/getters'
import * as mutations from '@/store/modules/measurements/mutations'

export const TIMEOUTS_IN_MS = {
  checkFrequency: 15000,
  clearValsFrequency: 180000
}

export const PRODUCER_DEVICES = ['chp', 'fuelcell', 'pv', 'wind']

/**
 * Store state for current `measurements`.
 *
 * `permission` - allows to store meta-data about device(s) and sensor(s) access permission. E.g. `hasPermissionRestriction` of `sensors` will be true, if the query hat NOT returned all requested sensors, because the user had access-permission restriction.
 *
 * @function
 *
 * @return {object}
 */

const state = () => ({
  devices: {},
  deviceRelations: {},
  sensors: {},
  seqs: {},
  vals: {},
  permission: {
    sensors: {
      hasPermissionRestriction: null
    }
  }
})

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

/**
 * A Sensor data-struct.
 *
 * @constructor
 *
 * @param {object} params
 * @param {string} params.id is the sensor's ID "emsId_sensorTypeId_countId"
 * @param {string} params.compositeId is the sensor's ID "emsId_sensorTypeId_countId"
 * @param {integer} params.sid is the "second" primary key
 * @param {integer} params.emsId is the Id of the Sensors EMS
 * @param {integer} params.countId is the countId of the Sensor
 * @param {string} params.label is the sensor's name returned from the BE.
 * @param {integer} params.countNum is the number of Sensors with this 'emsId.sensorTypeId' combination
 * @param {integer} params.flag is the kind of the sensor 'e.g instant, ref...'
 * @param {string} params.apiKey is the API reference key
 * @param {string} params.unit is the measured unit
 * @param {string} params.deviceId is the device id
 * @param {string} params.deviceType is the device type
 * @param {string} params.deviceLabel is the label of the device
 * @param {integer} params.sensorTypeFlag is the type of the sensor 'e.g. producer, consumer....'
 * @param {string} params.sensorTypeId is the Id of the sensortype 'e.g. pv_meter_demand....'
 * @param {boolean} params.isMeter shows whether the sensor is a meter or not
 * @param {string} params.refId is either the id of the reference sensor, or of the abstract sensor being referenced, depending on the flag
 * @param {string} params.refLabel is either the label of the reference sensor, or of the abstract sensor being referenced, depending on the flag
 * @param {string} params.refDeviceLabel is either the label of the reference sensor device, or of the abstract sensor device being referenced, depending on the flag
 * @param {string} params.type is 'sensors' or 'status_sensors'
 */
export class Sensor {
  constructor({
    id,
    compositeId,
    sid,
    emsId,
    countId,
    label,
    countNum,
    flag,
    apiKey,
    createdAt,
    unit,
    deviceId,
    deviceType,
    deviceLabel,
    sensorTypeFlag,
    sensorTypeId,
    type,
    isMeter,
    refId,
    refLabel,
    refDeviceLabel
  }) {
    this.id = id
    this.compositeId = compositeId || `${emsId}_${sensorTypeId}_${countId}`
    this.sid = sid
    this.emsId = emsId
    this.countId = parseInt(countId)
    this.label = label
    this.countNum = parseInt(countNum)
    this.flag = parseInt(flag)
    this.apiKey = apiKey
    this.createdAt = createdAt
    this.unit = unit
    this.deviceId = deviceId
    this.deviceType = deviceType
    this.deviceLabel = deviceLabel
    this.sensorTypeFlag = sensorTypeFlag
    this.sensorTypeId = sensorTypeId
    this.isMeter = isMeter
    this.refId = refId
    this.refLabel = refLabel
    this.refDeviceLabel = refDeviceLabel
    this.type = type
  }

  isOfType(type) {
    if (!SENSOR_FLAG_VALUES[type]) return false

    return (this.flag & SENSOR_FLAG_VALUES[type]) > 0
  }
  getDeviceLabel() {
    return this.refDeviceLabel ? `${this.refDeviceLabel} / ${this.deviceLabel}` : this.deviceLabel
  }
  getSensorName() {
    return this.refLabel || this.label
  }
}

export const DEVICE_FLAG_VALUES = {
  null: 0,
  physical_device: 1,
  iec_logical_device: 2
}

export const SENSOR_KINDS = {
  METER: 1,
  NON_METER: 2,
  INSTANT: 4,
  NON_INSTANT: 8,
  PAST: 16,
  BARE: 32,
  MEASURED_QUANTITY_IN_LABEL: 64,
  ENERGY_LABEL: 128,
  POWER_LABEL: 256,
  METER_LABEL: 512,
  NAKED_LABEL: 1024
}

export const SENSOR_FLAG_VALUES = {
  null: 0,
  virtual: 1,
  instantUpdate: 2,
  abstract: 4,
  reference: 8,
  instantUpdateFallback: 16,
  autoConfigProtected: 32,
  typed: 64
}

export const SENSOR_TYPE_FLAG_VALUES = {
  flowIntoSystem: 1,
  flowFromSystem: 2,
  producer: 4,
  consumer: 8,
  electricEnergy: 16,
  electricPower: 32,
  thermal: 64,
  thermalEnergy: 128,
  thermalPower: 256,
  abstract: 512,
  volumeCounter: 1024
}

export const THERMAL_FOR_DASHBOARD = ['heatpump_power', 'heatpump_temperature', 'heatingelement_power']

export const STATUS_SENSOR_TYPES_FOR_DASHBOARD = ['fire_alarm_state', 'wallbox_state']

/**
 * A Device data-struct.
 *
 * @constructor
 *
 * @param {object} params
 * @param {integer} params.id is the Devices ID
 * @param {string} params.deviceType is the Devices Type
 * @param {integer} params.countId is the countId of the Device
 * @param {integer} params.emsId is the Id of the Devices EMS
 * @param {integer} params.flag is the Deviceflag {see DEVICE_FLAG_VALUES}
 * @param {string} params.givenName is the potential user given name
 * @param {string} params.iecLdn is the devices iecLdn.
 * @param {integer} params.iecNodePostfix is the devices iecNodePostfix.
 * @param {string} params.iecNodePrefix is the devices iecNodePrefix.
 * @param {label} params.label is the devices label.
 * @param {string} params.defaultLabel is the default label (type + count ID), always present.
 * @param {integer}Array params.children is an Array of children deviceIds
 * @param {string}Array params.sensors is an Array of sensorIds
 *
 */
export class Device {
  constructor({
    id,
    deviceType,
    countId,
    emsId,
    flag,
    givenName,
    iecLdn,
    iecNodePostfix,
    iecNodePrefix,
    label,
    defaultLabel,
    children,
    sensors
  }) {
    this.id = parseInt(id)
    this.deviceType = deviceType
    this.countId = parseInt(countId)
    this.emsId = parseInt(emsId)
    this.flag = parseInt(flag)
    this.givenName = givenName
    this.iecLdn = iecLdn
    this.iecNodePostfix = parseInt(iecNodePostfix)
    this.iecNodePrefix = iecNodePrefix
    this.label = label
    this.defaultLabel = defaultLabel
    this.children = children || []
    this.sensors = sensors
  }

  isOfType(type) {
    if (!DEVICE_FLAG_VALUES[type]) return false

    return (this.flag & DEVICE_FLAG_VALUES[type]) > 0
  }
}

export const startOfYearMoment = (year) => moment().year(year).startOf('year').subtract(1, 'day')
export const endOfYearMoment = (year) => moment().year(year).endOf('year').add(1, 'day')
